import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';

import { AuthService } from '../../services/auth.service';
import { VolunteerService } from '../../services/volunteer.service';
import { Volunteer } from '../../interfaces/volunteer.interfaces';
import { StatusHistoryEntry } from '../../interfaces/student.interfaces';
import { environment } from '../../../environments/environment';
import { FolioPillComponent } from '../ui/folio-pill/folio-pill.component';

@Component({
  selector: 'app-volunteer-profile',
  standalone: true,
  imports: [CommonModule, FormsModule, FolioPillComponent],
  templateUrl: './volunteer-profile.html',
  styleUrl: './volunteer-profile.scss',
})
export class VolunteerProfile implements OnInit, OnDestroy {
  private authService = inject(AuthService);
  private volunteerService = inject(VolunteerService);
  private cdr = inject(ChangeDetectorRef);
  private destroyRef = inject(DestroyRef);

  volunteer: Volunteer | null = null;
  isLoading = true;
  isSaving = false;
  feedbackMessage = '';
  feedbackError = '';

  // Folio copiable (sin iconos, sombreado en hover)
  copiedFolio = signal<boolean>(false);
  private copyTimeout: any = null;

  // Selector de pestañas para Acreditaciones y Actividades
  accActiveTab: 'acreditaciones' | 'actividades' | 'todas' = 'todas';

  // Estados de edición independientes por sección
  isEditingPersonal = false;
  isEditingBio = false;
  isEditingSocials = false;

  // Respaldos para cancelar cambios independientes
  personalBackup = {
    phone: '',
  };

  bioBackup = {
    bioPersonal: '',
  };

  socialsBackup = {
    facebook: '',
    instagram: '',
    tiktok: '',
    x: '',
    youtube: '',
    linkedin: '',
  };

  // Documentos normativos institucionales
  legalDocs: Record<string, { title: string; url: string }> = {
    reglamento: {
      title: 'Reglamento General Interno',
      url: '',
    },
    privacidad: {
      title: 'Aviso de Privacidad Integral',
      url: '',
    },
    etica: {
      title: 'Código de Ética y Conducta',
      url: '',
    },
    incognito: {
      title: 'Permiso de Difusión Institucional',
      url: '',
    },
    consentimiento: {
      title: 'Consentimiento Voluntario Expreso',
      url: '',
    },
  };

  ngOnInit(): void {
    this.authService.user$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((user) => {
        if (!user?.id || user.role !== 'volunteer') {
          this.isLoading = false;
          this.cdr.detectChanges();
          return;
        }

        this.volunteerService
          .getVolunteerByAuthId(user.id)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: (volunteer) => {
              this.volunteer = this.ensureVolunteerDefaults(volunteer);
              this.isLoading = false;
              this.cdr.detectChanges();
            },
            error: (error) => {
              console.error('ERROR OBTENIENDO VOLUNTARIO EN PERFIL:', error);
              this.isLoading = false;
              this.cdr.detectChanges();
            },
          });
      });
  }

  ngOnDestroy(): void {
    if (this.copyTimeout) {
      clearTimeout(this.copyTimeout);
    }
  }

  private ensureVolunteerDefaults(v: Volunteer): Volunteer {
    if (!v.socialMedia) {
      v.socialMedia = {
        facebook: '',
        instagram: '',
        tiktok: '',
        x: '',
        youtube: '',
        linkedin: '',
      };
    }
    if (!v.assignedActivities) {
      v.assignedActivities = [];
    }
    if (!v.accreditations) {
      v.accreditations = [];
    }
    return v;
  }

  // =========================================
  // CONTROL DE PESTAÑAS (ACREDITACIONES / ACTIVIDADES)
  // =========================================
  setAccTab(tab: 'acreditaciones' | 'actividades' | 'todas'): void {
    this.accActiveTab = tab;
  }

  // =========================================
  // COPIAR FOLIO (CLIC-TO-COPY SIN ICONOS)
  // =========================================
  copyFolio(folio?: string): void {
    if (!folio) return;
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard
        .writeText(folio)
        .then(() => {
          this.triggerCopiedToast();
        })
        .catch(() => {
          this.fallbackCopy(folio);
        });
    } else {
      this.fallbackCopy(folio);
    }
  }

  private triggerCopiedToast(): void {
    this.copiedFolio.set(true);
    if (this.copyTimeout) {
      clearTimeout(this.copyTimeout);
    }
    this.copyTimeout = setTimeout(() => {
      this.copiedFolio.set(false);
      this.cdr.detectChanges();
    }, 2200);
    this.cdr.detectChanges();
  }

  private fallbackCopy(text: string): void {
    try {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      this.triggerCopiedToast();
    } catch {
      // Ignorar fallback error
    }
  }

  // =========================================
  // EDICIÓN SECCIÓN 1: INFORMACIÓN PERSONAL
  // =========================================
  startEditPersonal(): void {
    if (!this.volunteer) return;
    this.personalBackup = {
      phone: this.volunteer.phone || '',
    };
    this.isEditingPersonal = true;
  }

  cancelEditPersonal(): void {
    if (this.volunteer) {
      this.volunteer.phone = this.personalBackup.phone;
    }
    this.isEditingPersonal = false;
  }

  savePersonal(): void {
    if (!this.volunteer) return;
    this.isSaving = true;
    this.feedbackMessage = '';
    this.feedbackError = '';

    this.volunteerService
      .updateMyProfile({
        telefono: (this.volunteer.phone || '').trim(),
      })
      .subscribe({
        next: (updated) => {
          this.isSaving = false;
          this.isEditingPersonal = false;
          this.volunteer = this.ensureVolunteerDefaults(updated);
          this.feedbackMessage = 'Información de contacto actualizada correctamente.';
          this.cdr.detectChanges();
          setTimeout(() => (this.feedbackMessage = ''), 4000);
        },
        error: (err) => {
          this.isSaving = false;
          this.feedbackError =
            err?.error?.message || 'Error al actualizar información personal.';
          this.cdr.detectChanges();
        },
      });
  }

  // =========================================
  // EDICIÓN SECCIÓN 2: SEMBLANZA / BIO
  // =========================================
  startEditBio(): void {
    if (!this.volunteer) return;
    this.bioBackup = {
      bioPersonal: this.volunteer.bioPersonal || '',
    };
    this.isEditingBio = true;
  }

  cancelEditBio(): void {
    if (this.volunteer) {
      this.volunteer.bioPersonal = this.bioBackup.bioPersonal;
    }
    this.isEditingBio = false;
  }

  saveBio(): void {
    if (!this.volunteer) return;
    this.isSaving = true;
    this.feedbackMessage = '';
    this.feedbackError = '';

    this.volunteerService
      .updateMyProfile({
        bioPersonal: (this.volunteer.bioPersonal || '').trim(),
      })
      .subscribe({
        next: (updated) => {
          this.isSaving = false;
          this.isEditingBio = false;
          this.volunteer = this.ensureVolunteerDefaults(updated);
          this.feedbackMessage = 'Semblanza actualizada correctamente.';
          this.cdr.detectChanges();
          setTimeout(() => (this.feedbackMessage = ''), 4000);
        },
        error: (err) => {
          this.isSaving = false;
          this.feedbackError =
            err?.error?.message || 'Error al actualizar semblanza.';
          this.cdr.detectChanges();
        },
      });
  }

  // =========================================
  // EDICIÓN SECCIÓN 3: REDES SOCIALES
  // =========================================
  startEditSocials(): void {
    if (!this.volunteer) return;
    const sm = this.volunteer.socialMedia || ({} as any);
    this.socialsBackup = {
      facebook: sm.facebook || '',
      instagram: sm.instagram || '',
      tiktok: sm.tiktok || '',
      x: sm.x || '',
      youtube: sm.youtube || '',
      linkedin: sm.linkedin || '',
    };
    this.isEditingSocials = true;
  }

  cancelEditSocials(): void {
    if (this.volunteer) {
      if (!this.volunteer.socialMedia) {
        this.volunteer.socialMedia = {} as any;
      }
      Object.assign(this.volunteer.socialMedia, this.socialsBackup);
    }
    this.isEditingSocials = false;
  }

  saveSocials(): void {
    if (!this.volunteer) return;
    this.isSaving = true;
    this.feedbackMessage = '';
    this.feedbackError = '';

    const sm = this.volunteer.socialMedia || ({} as any);
    this.volunteerService
      .updateMyProfile({
        facebookUsuario: (sm.facebook || '').trim(),
        instagramUsuario: (sm.instagram || '').trim(),
        tiktokUsuario: (sm.tiktok || '').trim(),
        xUsuario: (sm.x || '').trim(),
        youtubeUsuario: (sm.youtube || '').trim(),
        linkedinUsuario: (sm.linkedin || '').trim(),
      })
      .subscribe({
        next: (updated) => {
          this.isSaving = false;
          this.isEditingSocials = false;
          this.volunteer = this.ensureVolunteerDefaults(updated);
          this.feedbackMessage = 'Redes sociales actualizadas correctamente.';
          this.cdr.detectChanges();
          setTimeout(() => (this.feedbackMessage = ''), 4000);
        },
        error: (err) => {
          this.isSaving = false;
          this.feedbackError =
            err?.error?.message || 'Error al actualizar redes sociales.';
          this.cdr.detectChanges();
        },
      });
  }

  // =========================================
  // DOCUMENTOS Y CONSTANCIAS
  // =========================================
  hasValidDocument(fileUrl: string | null | undefined): boolean {
    if (!fileUrl || fileUrl === 'pending') return false;
    const trimmed = fileUrl.trim();
    return trimmed.length > 0 && (trimmed.startsWith('http://') || trimmed.startsWith('https://') || trimmed.startsWith('/'));
  }

  openDocument(fileUrl: string | null | undefined): void {
    if (!fileUrl || fileUrl === 'pending') return;
    window.open(fileUrl, '_blank', 'noopener,noreferrer');
  }

  openAcceptanceCertificate(): void {
    if (this.volunteer?.cartaAceptacion && this.volunteer.cartaAceptacion.startsWith('http')) {
      window.open(this.volunteer.cartaAceptacion, '_blank', 'noopener,noreferrer');
      return;
    }
    const id = (this.volunteer as any)?._id || this.volunteer?.id;
    if (id) {
      window.open(`${environment.apiUrl}/volunteer/${id}/certificate/acceptance`, '_blank', 'noopener,noreferrer');
    }
  }

  getLegalDocUrl(docKey: string): string {
    const doc = this.legalDocs[docKey];
    return doc?.url || '#';
  }

  openLegalDoc(docKey: string, event?: Event): void {
    const doc = this.legalDocs[docKey];
    const url = doc?.url;
    if (url && url.startsWith('http')) {
      return;
    }

    if (event) {
      event.preventDefault();
    }

    const win = window.open('', '_blank');
    if (win) {
      const docTitle = doc?.title || 'Documento Normativo Institucional';
      win.document.write(`
        <!DOCTYPE html>
        <html lang="es">
        <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>${docTitle} · ABAQ</title>
          <style>
            body {
              font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
              display: flex;
              align-items: center;
              justify-content: center;
              min-height: 100vh;
              margin: 0;
              background: #f8fafc;
              color: #1e293b;
              padding: 24px;
              box-sizing: border-box;
            }
            .card {
              max-width: 540px;
              width: 100%;
              padding: 40px 32px;
              background: #ffffff;
              border-radius: 20px;
              border: 1px solid #e2e8f0;
              box-shadow: 0 16px 36px rgba(36, 54, 111, 0.08);
              text-align: center;
            }
            .badge {
              display: inline-block;
              background: #ecfdf5;
              color: #065f46;
              border: 1px solid #a7f3d0;
              padding: 5px 14px;
              border-radius: 999px;
              font-size: 11.5px;
              font-weight: 800;
              letter-spacing: 0.5px;
              margin-bottom: 20px;
              text-transform: uppercase;
            }
            h1 {
              color: #24366f;
              margin: 0 0 12px;
              font-size: 22px;
              font-weight: 800;
            }
            p {
              color: #64748b;
              line-height: 1.6;
              font-size: 14.5px;
              margin: 0 0 24px;
            }
            .info-box {
              background: #f1f5f9;
              border-radius: 12px;
              padding: 16px;
              font-size: 13px;
              color: #334155;
              border: 1px solid #cbd5e1;
            }
          </style>
        </head>
        <body>
          <div class="card">
            <span class="badge">✓ Validado en Expediente Digital</span>
            <h1>${docTitle}</h1>
            <p>Este documento normativo fue revisado y aceptado expresamente durante el proceso de registro institucional en ABAQ Querétaro.</p>
            <div class="info-box">
              📄 La versión digital descargable para consulta pública se encuentra en preparación y estará vinculada a este enlace próximamente.
            </div>
          </div>
        </body>
        </html>
      `);
      win.document.close();
    }
  }

  // =========================================
  // ACTIVIDADES Y FORMATO (CERO HORAS)
  // =========================================
  getActivityTitle(act: string): string {
    if (!act) return '';
    return act.replace(/\s*\((?:Acredita|Horas)[\s\S]*?\)/gi, '').trim();
  }

  getAccreditationTypeLabel(type?: string | null): string {
    switch (type) {
      case 'EVENT':
        return 'Evento ABAQ';
      case 'INDUCTION':
        return 'Inducción';
      case 'IDENTIFICATION':
        return 'Placas de identificación';
      case 'STERILIZATION':
        return 'Esterilización';
      case 'ADOPTION':
        return 'Adopción';
      case 'ADOPTION_MODULE':
        return 'Módulo de adopción';
      case 'TALK':
        return 'Plática ABAQ';
      case 'CAMPAIGN':
        return 'Campaña';
      case 'MEDIA':
        return 'Difusión en medios';
      case 'SOCIAL_MEDIA':
        return 'Redes sociales';
      case 'DONATION':
        return 'Recaudación de fondos';
      case 'ADMINISTRATIVE':
        return 'Actividad administrativa';
      default:
        return type || 'Acreditación institucional';
    }
  }

  getAccreditationStatusLabel(status?: string | null): string {
    switch (status) {
      case 'approved':
      case 'accredited':
        return 'Aprobada';
      case 'pending':
        return 'En revisión';
      case 'rejected':
        return 'Rechazada';
      default:
        return status || 'Registrada';
    }
  }

  getActivityStatusLabel(status?: string | null): string {
    switch (status) {
      case 'registered':
        return 'Registrado';
      case 'attended':
        return 'Asistió';
      case 'validated':
        return 'Asistencia acreditada';
      case 'absent':
        return 'No asistió';
      case 'cancelled':
        return 'Cancelada';
      default:
        return status || 'Registrado';
    }
  }

  // =========================================
  // CALIFICACIÓN DE DESEMPEÑO
  // =========================================
  getPerformanceLabel(status?: string | null, performance?: string | null): string {
    const s = status || '';
    const p = performance || '';
    if (s === 'absent' || p === 'absent') return 'Inasistencia';
    const val = p && p !== 'pending' ? p : (s && s !== 'registered' && s !== 'attended' && s !== 'validated' ? s : '');
    switch (val) {
      case 'excellent':
        return 'Excelente';
      case 'very_good':
        return 'Muy bueno';
      case 'good':
        return 'Bueno';
      case 'regular':
        return 'Regular';
      case 'bad':
        return 'Malo';
      case 'very_bad':
        return 'Muy malo';
      default:
        return 'Pendiente de evaluación';
    }
  }

  getPerformanceClass(status?: string | null, performance?: string | null): string {
    const s = status || '';
    const p = performance || '';
    if (s === 'absent' || p === 'absent') return 'perf-absent';
    const val = p && p !== 'pending' ? p : (s && s !== 'registered' && s !== 'attended' && s !== 'validated' ? s : '');
    switch (val) {
      case 'excellent':
      case 'very_good':
        return 'perf-good';
      case 'good':
      case 'regular':
        return 'perf-neutral';
      case 'bad':
      case 'very_bad':
        return 'perf-bad';
      default:
        return 'perf-pending';
    }
  }

  // =========================================
  // REFLEXIONES DE INDUCCIÓN
  // =========================================
  get reflectionsList() {
    const refs = this.volunteer?.onboardingData?.reflections || {};
    const modules = [
      { id: 'modulo-1', name: 'Módulo 1: Historia, Misión y Visión de ABAQ' },
      { id: 'modulo-2', name: 'Módulo 2: Valores y Cultura del Voluntariado' },
      { id: 'modulo-3', name: 'Módulo 3: Bienestar y Rescate Animal' },
      { id: 'modulo-4', name: 'Módulo 4: Protocolos de Seguridad y Convivencia' },
      { id: 'modulo-5', name: 'Módulo 5: Adopciones Responsables y Campañas' },
      { id: 'modulo-6', name: 'Módulo 6: Organización de Eventos y Difusión' },
    ];
    return modules.map((m) => ({
      ...m,
      data: refs[m.id] || null,
    }));
  }

  hasAnyReflections(): boolean {
    const refs = this.volunteer?.onboardingData?.reflections;
    return !!refs && Object.keys(refs).length > 0;
  }

  // =========================================
  // ESTADÍSTICAS Y CONTADORES
  // =========================================
  get opportunitiesCount(): number {
    return (this.volunteer as any)?.accreditations?.length || 0;
  }

  get activitiesCount(): number {
    return this.volunteer?.assignedActivities?.length || 0;
  }

  get totalRecordsCount(): number {
    return this.opportunitiesCount + this.activitiesCount;
  }

  get lastStatusChange(): StatusHistoryEntry | null {
    const history = this.volunteer?.statusHistory;
    if (!history || history.length === 0) {
      return null;
    }
    return [...history].sort((a, b) => {
      const da = a.date ? new Date(a.date).getTime() : 0;
      const db = b.date ? new Date(b.date).getTime() : 0;
      return db - da;
    })[0];
  }

  getFriendlyReason(reason?: string): string {
    if (!reason) return 'Decisión administrativa institucional';
    const map: Record<string, string> = {
      initial_signup: 'Registro inicial completado',
      admin_approval: 'Aprobación oficial de expediente',
      admin_rejection: 'Rechazo con observaciones de Coordinación',
      hours_completed: 'Conclusión satisfactoria de 480 horas de servicio',
      administrative_decision: 'Baja administrativa institucional',
      re_registration: 'Re-registro y corrección de expediente',
    };
    return map[reason] || reason;
  }

  get hasAnyFolios(): boolean {
    if (this.volunteer?.folio) return true;
    const actFolios = (this.volunteer?.assignedActivities || []).some(
      (a) => !!a.interactionFolio,
    );
    const accFolios = ((this.volunteer as any)?.accreditations || []).some(
      (a: any) => !!a.interactionFolio,
    );
    return actFolios || accFolios;
  }
}
